import React, { useState, useEffect } from "react";
import InfoIcon from "../../assets/icons/info.svg";
import "./MiniCalendar.css";
import { attendanceActions } from "../../store/AttendanceSlice";
import { useDispatch, useSelector } from "react-redux";
import { getLeaveCountBetweenDates } from "../../store/AttendanceSlice/thunk";
import moment from "moment";

const HALF_DAY = 12 * 60 * 60 * 1000;

const MiniCalendar = ({
  onDateSelect,
  minDate,
  maxDate,
  leaveFormData,
  onStartChange,
  disableHalfMode = false,
}) => {
  const dispatch = useDispatch();
  const halfMode = useSelector((state) => state.attendance.halfMode);
  const employeeID = useSelector((state) => state.auth.employeeID);
  const totalDays = useSelector(
    (state) => state.attendance.leaveCountResponse?.total_count,
  );
  // console.log("totalDays in MiniCalendar:", totalDays);
  // console.log("leaveFormData in MiniCalendar:", leaveFormData);

  const today = new Date();

  const [currentDate, setCurrentDate] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1),
  );

  const [start, setStart] = useState(null);
  const [end, setEnd] = useState(null);
  const [hoverDate, setHoverDate] = useState(null);
  const [halfHover, setHalfHover] = useState(null);

  const [halfStart, setHalfStart] = useState(null);
  const [halfEnd, setHalfEnd] = useState(null);

  const [halfdayError, setHalfdayError] = useState(false);
  const [halfStartType, setHalfStartType] = useState({});

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const monthName = currentDate.toLocaleString("default", {
    month: "long",
  });

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = new Date(year, month, 1).getDay();

  const firstAllowedMonth = minDate
    ? new Date(minDate.getFullYear(), minDate.getMonth(), 1)
    : null;

  const lastAllowedMonth = (() => {
    if (!maxDate && !leaveFormData?.end) return null;

    const maxMonth = maxDate
      ? new Date(maxDate.getFullYear(), maxDate.getMonth(), 1)
      : null;

    const endMonth = leaveFormData?.end
      ? new Date(
          new Date(leaveFormData.end).getFullYear(),
          new Date(leaveFormData.end).getMonth(),
          1,
        )
      : null;

    if (maxMonth && endMonth) {
      return endMonth > maxMonth ? endMonth : maxMonth;
    }

    return endMonth || maxMonth;
  })();

  const prevDisabled =
    firstAllowedMonth &&
    year === firstAllowedMonth.getFullYear() &&
    month === firstAllowedMonth.getMonth();

  const nextDisabled =
    lastAllowedMonth &&
    year === lastAllowedMonth.getFullYear() &&
    month === lastAllowedMonth.getMonth();

  const changeMonth = (direction) => {
    const newDate = new Date(year, month + direction, 1);

    if (
      (firstAllowedMonth && newDate < firstAllowedMonth) ||
      (lastAllowedMonth && newDate > lastAllowedMonth)
    ) {
      return;
    }

    setCurrentDate(newDate);
  };

  const resetSelection = () => {
    setStart(null);
    setEnd(null);
    setHoverDate(null);
    setHalfHover(null);
    setHalfStart(null);
    setHalfStartType({});
    setHalfEnd(null);
    setHalfdayError(false);
  };

  useEffect(() => {
    if (leaveFormData?.start) {
      setStart(new Date(leaveFormData.start));
    } else {
      setStart(null);
    }

    if (leaveFormData?.end) {
      setEnd(new Date(leaveFormData.end));
    } else {
      setEnd(null);
    }
  }, [leaveFormData]);

  useEffect(() => {
    if (leaveFormData?.leave_type !== undefined) {
      resetSelection();
      dispatch(
        attendanceActions.setLeaveData({
          start: null,
          end: null,
          applied_days: 0,
        }),
      );
      onDateSelect?.({
        start: null,
        end: null,
        applied_days: 0,
      });
    }
  }, [leaveFormData?.leave_type, dispatch, onDateSelect]);

  const getWorkingDaysBetweenDates = (startDate, endDate) => {
    dispatch(
      getLeaveCountBetweenDates({
        employee_id: employeeID,
        start_date: moment(startDate).format("DD-MM-YY"),
        end_date: moment(endDate).format("DD-MM-YY"),
      }),
    );
  };

  const handleFullClick = (day) => {
    const clickedDate = new Date(year, month, day);

    if (!start || (start && end)) {
      setStart(clickedDate);
      setEnd(null);
      setHoverDate(null);
      onStartChange(clickedDate);
      return;
    }

    const rangeStart = clickedDate < start ? clickedDate : start;
    const rangeEnd = clickedDate > start ? clickedDate : start;

    getWorkingDaysBetweenDates(rangeStart, rangeEnd);
    const totalDays =
      Math.floor((rangeEnd - rangeStart) / (1000 * 60 * 60 * 24)) + 1;

    const data = {
      start: rangeStart,
      end: rangeEnd,
      applied_days: totalDays,
    };

    setEnd(clickedDate);
    setHoverDate(null);
    dispatch(attendanceActions.setLeaveData(data));
    onDateSelect(data);
  };

  const getRangeInfo = (day) => {
    if (!start) return null;

    const current = new Date(year, month, day);
    const effectiveEnd = end || hoverDate;

    if (!effectiveEnd) {
      if (current.getTime() === start.getTime()) {
        return { isSingle: true };
      }
      return null;
    }

    const min = start < effectiveEnd ? start : effectiveEnd;
    const max = start > effectiveEnd ? start : effectiveEnd;

    if (current < min || current > max) return null;

    return {
      isStart: current.getTime() === min.getTime(),
      isEnd: current.getTime() === max.getTime(),
      isMiddle: current > min && current < max,
      isSingle: min.getTime() === max.getTime(),
      isPreview: !end,
    };
  };

  const getHalfValue = (day, type) => {
    const base = new Date(year, month, day).getTime();
    return type === "first" ? base : base + HALF_DAY;
  };

  const handleHalfClick = (day, type) => {
    setHalfdayError(false);

    if (halfStart && !halfEnd) {
      if (
        (type === "first" &&
          halfStartType?.type === "second" &&
          day > halfStartType?.day) ||
        (type === "second" &&
          halfStartType?.type === "first" &&
          day < halfStartType?.day)
      ) {
        setHalfdayError(true);
        setHalfEnd(null);
        return;
      }
    }

    const value = getHalfValue(day, type);

    if (!halfStart || (halfStart && halfEnd)) {
      setHalfStart(value);
      setHalfStartType({ type, day });
      setHalfEnd(null);
      setHalfHover(null);
      return;
    }

    const isStartSecondHalf = halfStart % (24 * 60 * 60 * 1000) !== 0;
    const isEndFirstHalf = value % (24 * 60 * 60 * 1000) === 0;

    if (isStartSecondHalf && isEndFirstHalf) {
      setHalfdayError(true);
      setHalfEnd(null);
      return;
    }

    const startType = halfStartType.type;
    const endType = type;

    setHalfEnd(value);
    setHalfHover(null);
    const newStart = Math.min(halfStart, value);
    const newEnd = Math.max(halfStart, value);

    let halfType = updateHalfDayState(startType, endType);

    getWorkingDaysBetweenDates(new Date(newStart), new Date(newEnd));
    const totalDays = (newEnd - newStart) / (24 * 60 * 60 * 1000) + 0.5;

    const data = {
      start: new Date(newStart),
      end: new Date(newEnd),
      applied_days: totalDays,
      halfDaysSelected: halfType,
    };

    dispatch(attendanceActions.setLeaveData(data));
    onDateSelect(data);
  };

  const updateHalfDayState = (startType, endType) => {
    if (startType === "first" && endType === "first") {
      return {
        first_half: "present",
        second_half: "half_day",
      };
    }
    if (startType === "second" && endType === "second") {
      return {
        first_half: "half_day",
        second_half: "present",
      };
    }
    if (startType === "first" && endType === "second") {
      return {
        first_half: "present",
        second_half: "present",
      };
    }

    return {
      first_half: "present",
      second_half: "present",
    };
  };

  const getHalfRangeInfo = (day) => {
    if (!halfStart) return null;

    const base = new Date(year, month, day).getTime();

    const s = halfStart;
    const e = halfEnd ?? halfHover ?? halfStart;

    const min = Math.min(s, e);
    const max = Math.max(s, e);

    return {
      firstActive: base >= min && base <= max,
      secondActive: base + HALF_DAY >= min && base + HALF_DAY <= max,
      isPreview: !halfEnd,
    };
  };

  const displayedDays = totalDays
    ? totalDays -
      (leaveFormData?.applied_days % 1 === 0.5 &&
      leaveFormData?.end !== leaveFormData?.start
        ? 0.5
        : 0)
    : leaveFormData?.applied_days;

  return (
    <div className="minicalendar">
      <div className="mini-top">
        <span className="mini-count-box">
          <div className="mini-count-head">dates</div>
          <div className="mini-count-wrap">
            <div className="mini-count">
              {displayedDays || 0}
              &nbsp;
              {displayedDays === "1" ? "day" : "days"}
            </div>
          </div>
        </span>

        <div className="mini-toggle">
          <span className="mini-toggle-text">Half Day</span>

          <label className="switch">
            <input
              type="checkbox"
              checked={halfMode}
              onChange={() => {
                dispatch(attendanceActions.setHalfMode(!halfMode));
                resetSelection();
              }}
              disabled={disableHalfMode}
            />
            <span className="slider"></span>
          </label>
        </div>
      </div>

      <div className="minicalendar-box">
        <div className="minicalendar-subbbox">
          <div className="minicalendar-header">
            <div className="month-title">
              {monthName} {year}
            </div>

            <div className="flex items-center">
              <div
                className={`mini-nav-btn ${prevDisabled ? "disabled" : ""}`}
                onClick={() => !prevDisabled && changeMonth(-1)}
              >
                ‹
              </div>

              <div
                className={`mini-nav-btn ${nextDisabled ? "disabled" : ""}`}
                onClick={() => !nextDisabled && changeMonth(1)}
              >
                ›
              </div>
            </div>
          </div>

          <div className="mini-week">
            {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
              <div key={d + i}>{d}</div>
            ))}
          </div>

          <div className="mini-grid">
            {[...Array(firstDay)].map((_, i) => (
              <div key={"empty" + i}></div>
            ))}

            {[...Array(daysInMonth)].map((_, i) => {
              const day = i + 1;
              const dayDate = new Date(year, month, day);

              const isDisabled =
                (minDate && dayDate < minDate) ||
                (maxDate && dayDate > maxDate);

              const isToday = dayDate.toDateString() === today.toDateString();

              const rangeInfo = getRangeInfo(day);
              const halfInfo = getHalfRangeInfo(day);

              return (
                <div
                  key={day}
                  className={`mini-cell ${halfMode ? "mini-hover" : "cell-hover"} ${isDisabled ? "disabled" : ""} ${
                    isToday ? "today" : ""
                  }`}
                  onClick={() =>
                    !halfMode && !isDisabled && handleFullClick(day)
                  }
                  onMouseEnter={() => {
                    if (start && !end && !isDisabled) {
                      setHoverDate(new Date(year, month, day));
                    }
                  }}
                  onMouseLeave={() => {
                    if (!end) setHoverDate(null);
                  }}
                >
                  {!halfMode && rangeInfo && (
                    <div
                      className={`range 
                        ${rangeInfo.isStart ? "start" : ""}
                        ${rangeInfo.isEnd ? "end" : ""}
                        ${rangeInfo.isMiddle ? "middle" : ""}
                        ${rangeInfo.isSingle ? "single" : ""}
                        ${rangeInfo.isPreview ? "preview" : ""}
                      `}
                    />
                  )}
                  {halfMode && (
                    <>
                      <div
                        className={`half-cell left ${
                          halfInfo?.firstActive ? "active" : ""
                        } ${halfInfo?.isPreview ? "preview" : ""}`}
                        onMouseEnter={() => {
                          if (halfStart && !halfEnd && !isDisabled) {
                            setHalfHover(getHalfValue(day, "first"));
                          }
                        }}
                        onMouseLeave={() => {
                          if (!halfEnd) setHalfHover(null);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          !isDisabled && handleHalfClick(day, "first");
                        }}
                      />

                      <div
                        className={`half-cell right ${
                          halfInfo?.secondActive ? "active" : ""
                        } ${halfInfo?.isPreview ? "preview" : ""}`}
                        onMouseEnter={() => {
                          if (halfStart && !halfEnd && !isDisabled) {
                            setHalfHover(getHalfValue(day, "second"));
                          }
                        }}
                        onMouseLeave={() => {
                          if (!halfEnd) setHalfHover(null);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          !isDisabled && handleHalfClick(day, "second");
                        }}
                      />
                    </>
                  )}

                  <span>{day}</span>
                </div>
              );
            })}
          </div>
        </div>

        {halfdayError && (
          <div className="half-error-wrapper">
            <img className="half-error-icon" src={InfoIcon} alt="Error" />
            <div className="half-error-message">
              Only one half-day can be selected in a date range.
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MiniCalendar;
